import type { NotificationCounts, SidebarResponse, User } from '@cyphex/types';
import { apiGet } from './client';
import { USE_MOCK } from './env';
import { MOCK_NOTIFICATION_COUNTS, MOCK_SIDEBAR, MOCK_VIEWER } from './mock/shellFixtures';

/**
 * Everything the app shell needs. The mock adapter returns the SAME types the
 * HTTP adapter does, so `VITE_USE_MOCK` swaps the whole shell between a live
 * backend and an offline demo without a single component change.
 */
export interface ShellApi {
  /** GET /api/v1/me */
  getViewer(signal?: AbortSignal): Promise<User>;
  /** GET /api/v1/me/sidebar — one call powers the whole left rail. */
  getSidebar(signal?: AbortSignal): Promise<SidebarResponse>;
  /** GET /api/v1/notifications/counts */
  getNotificationCounts(signal?: AbortSignal): Promise<NotificationCounts>;
}

/** `/me` carries identity + tenant + permissions; the shell renders the user. */
interface MeResponse {
  user: User;
}

const httpShellApi: ShellApi = {
  getViewer: (signal) => apiGet<MeResponse>('/me', signal).then((payload) => payload.user),
  getSidebar: (signal) => apiGet<SidebarResponse>('/me/sidebar', signal),
  getNotificationCounts: (signal) => apiGet<NotificationCounts>('/notifications/counts', signal),
};

const MOCK_LATENCY_MS = 120;

const settle = <T>(value: T): Promise<T> =>
  new Promise((resolve) => {
    setTimeout(() => resolve(value), MOCK_LATENCY_MS);
  });

const mockShellApi: ShellApi = {
  getViewer: () => settle(MOCK_VIEWER),
  getSidebar: () => settle(MOCK_SIDEBAR),
  getNotificationCounts: () => settle(MOCK_NOTIFICATION_COUNTS),
};

export const shellApi: ShellApi = USE_MOCK ? mockShellApi : httpShellApi;
